import { useState } from "react";
import {
  getPriorityWeights,
  updatePriorityWeights,
  proposePriorityWeights,
  getPriorityProfile,
} from "./PriorityService";
import { ANALYZER_LABELS } from "./PriorityPanel";
import { useToast } from "../ToastContext";

const SOURCE_LABELS = {
  DEFAULT: "System default",
  MANUAL: "Manually tuned",
  LEARNED: "Learned from your feedback",
};

/**
 * Phase 5 (Personalization) — per-user priority-weight tuning + the
 * feedback-driven proposal/backtest workflow, plus a read-only intelligence
 * profile. Deliberately its own standalone panel (sibling of PriorityPanel/
 * DependenciesPanel, not nested inside either) — same "collapsed by default,
 * user opts in" shape as those, since none of this needs to load on every
 * form open.
 */
export default function PriorityWeightsPanel() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [weightsDto, setWeightsDto] = useState(null); // { weights, source, updatedAt }
  const [edited, setEdited] = useState(null); // controlled percent values while editing
  const [saving, setSaving] = useState(false);

  const [proposal, setProposal] = useState(null);
  const [proposing, setProposing] = useState(false);

  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const dto = await getPriorityWeights();
      setWeightsDto(dto);
      setEdited(toPercents(dto.weights));
    } catch {
      toast("Failed to load priority weights", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && !weightsDto) await load();
  };

  const saveWeights = async () => {
    setSaving(true);
    try {
      const dto = await updatePriorityWeights(fromPercents(edited), "MANUAL");
      setWeightsDto(dto);
      setEdited(toPercents(dto.weights));
      setProposal(null);
      toast("Priority weights saved", "success");
    } catch {
      toast("Failed to save weights", "error");
    } finally {
      setSaving(false);
    }
  };

  const runProposal = async () => {
    setProposing(true);
    try {
      setProposal(await proposePriorityWeights());
    } catch {
      toast("Failed to compute weight proposal", "error");
    } finally {
      setProposing(false);
    }
  };

  const activateProposal = async () => {
    if (!proposal) return;
    setSaving(true);
    try {
      const dto = await updatePriorityWeights(proposal.proposedWeights, "LEARNED");
      setWeightsDto(dto);
      setEdited(toPercents(dto.weights));
      setProposal(null);
      toast("Learned weights activated", "success");
    } catch {
      toast("Failed to activate proposed weights", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleProfile = async () => {
    const next = !showProfile;
    setShowProfile(next);
    if (next && !profile) {
      setProfileLoading(true);
      try {
        setProfile(await getPriorityProfile());
      } catch {
        toast("Failed to load intelligence profile", "error");
      } finally {
        setProfileLoading(false);
      }
    }
  };

  return (
    <div className="priw-panel">
      <button type="button" className="priw-toggle" onClick={toggleOpen}>
        {open ? "▾" : "▸"} 🎛️ Personalize priority weights
        {weightsDto && <span className="priw-source-badge">{SOURCE_LABELS[weightsDto.source] || weightsDto.source}</span>}
      </button>

      {open && (
        <div className="priw-body">
          {loading && <div className="priw-loading">Loading…</div>}

          {!loading && edited && (
            <>
              <div className="priw-sliders">
                {Object.keys(edited).map(key => (
                  <div key={key} className="priw-slider-row">
                    <span className="priw-slider-label">{ANALYZER_LABELS[key] || key}</span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={edited[key]}
                      onChange={e => setEdited({ ...edited, [key]: Number(e.target.value) })}
                    />
                    <span className="priw-slider-value">{Math.round(edited[key])}%</span>
                  </div>
                ))}
              </div>
              <div className="priw-actions">
                <button type="button" className="priw-save-btn" onClick={saveWeights} disabled={saving}>
                  {saving ? "Saving…" : "Save weights"}
                </button>
                <button type="button" className="priw-propose-btn" onClick={runProposal} disabled={proposing}>
                  {proposing ? "Analyzing feedback…" : "🔮 Suggest from my feedback"}
                </button>
              </div>
            </>
          )}

          {proposal && (
            <div className="priw-proposal">
              {proposal.feedbackConsidered === 0 ? (
                <div className="priw-proposal-empty">
                  {proposal.backtest?.notes?.[0] || "Not enough feedback yet to propose a change."}
                </div>
              ) : (
                <>
                  <div className="priw-proposal-head">
                    Based on {proposal.feedbackConsidered} feedback entr{proposal.feedbackConsidered === 1 ? "y" : "ies"}:
                  </div>
                  <div className="priw-deltas">
                    {proposal.deltas.filter(d => Math.abs(d.delta) > 0.001).map(d => (
                      <div key={d.analyzer} className="priw-delta-row">
                        <span className="priw-delta-name">{ANALYZER_LABELS[d.analyzer] || d.analyzer}</span>
                        <span className="priw-delta-change">
                          {Math.round(d.currentWeight * 100)}% → {Math.round(d.proposedWeight * 100)}%
                        </span>
                        <span className="priw-delta-reason">{d.reason}</span>
                      </div>
                    ))}
                    {proposal.deltas.every(d => Math.abs(d.delta) <= 0.001) && (
                      <div className="priw-proposal-empty">No net change suggested by your feedback so far.</div>
                    )}
                  </div>
                  {proposal.backtest && (
                    <div className="priw-backtest">
                      <span className="priw-backtest-stat priw-backtest-stat--good">✓ {proposal.backtest.improved} improved</span>
                      <span className="priw-backtest-stat priw-backtest-stat--neutral">= {proposal.backtest.preserved} unchanged</span>
                      {proposal.backtest.regressed > 0 && (
                        <span className="priw-backtest-stat priw-backtest-stat--bad">✗ {proposal.backtest.regressed} regressed</span>
                      )}
                      {proposal.backtest.broken > 0 && (
                        <span className="priw-backtest-stat priw-backtest-stat--bad">⚠ {proposal.backtest.broken} tier-changed</span>
                      )}
                    </div>
                  )}
                  {proposal.backtest?.notes?.map((n, i) => (
                    <div key={i} className="priw-backtest-note">{n}</div>
                  ))}
                  <button type="button" className="priw-activate-btn" onClick={activateProposal} disabled={saving}>
                    {saving ? "Activating…" : "✅ Activate these weights"}
                  </button>
                </>
              )}
            </div>
          )}

          <button type="button" className="priw-profile-toggle" onClick={toggleProfile}>
            {showProfile ? "Hide" : "Show"} my intelligence profile
          </button>

          {showProfile && (
            <div className="priw-profile">
              {profileLoading && <div className="priw-loading">Loading…</div>}
              {!profileLoading && profile && (
                <>
                  <div className="priw-profile-row">
                    <span className="priw-profile-label">Feedback acceptance</span>
                    <span className="priw-profile-value">
                      {profile.acceptanceRate == null
                        ? "No feedback yet"
                        : `${Math.round(profile.acceptanceRate * 100)}% accurate (${profile.feedbackAccurate}/${profile.feedbackTotal})`}
                    </span>
                  </div>
                  <div className="priw-profile-row">
                    <span className="priw-profile-label">Overall punctuality</span>
                    <span className="priw-profile-value">
                      {profile.overallPunctuality == null
                        ? `Not enough completions yet (${profile.completionsConsidered})`
                        : `${Math.round(profile.overallPunctuality * 100)}% on time`}
                    </span>
                  </div>

                  {profile.categoryEffortStats?.length > 0 && (
                    <div className="priw-category-stats">
                      <div className="priw-profile-label">By category</div>
                      {profile.categoryEffortStats.map(c => (
                        <div key={c.category} className="priw-category-row">
                          <span className="priw-category-name">{c.category}</span>
                          <span className="priw-category-detail">{c.completionCount} done</span>
                          {c.avgActualMinutes != null && (
                            <span className="priw-category-detail">~{Math.round(c.avgActualMinutes)} min avg</span>
                          )}
                          {c.onTimeRatio != null && (
                            <span className="priw-category-detail">{Math.round(c.onTimeRatio * 100)}% on time</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {profile.postponementHotspots?.length > 0 && (
                    <div className="priw-hotspots">
                      <div className="priw-profile-label">Most postponed</div>
                      {profile.postponementHotspots.map(h => (
                        <div key={h.itemId} className="priw-hotspot-row">
                          <span className="priw-hotspot-title">{h.itemTitle}</span>
                          <span className="priw-hotspot-count">{h.postponeCount}×</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function toPercents(weights) {
  const out = {};
  Object.entries(weights || {}).forEach(([k, v]) => { out[k] = Math.round(v * 100); });
  return out;
}

function fromPercents(percents) {
  const out = {};
  Object.entries(percents || {}).forEach(([k, v]) => { out[k] = v / 100; });
  return out;
}
